import React, { useState, useEffect, useRef } from "react"
import { Box, Button, TextField, Select, MenuItem } from '@material-ui/core'
import { useStyles } from './styles'
import EventsTable from './EventsTable'
import { EventTableHeadings, SortOrder, REQUEST_BODY_PLACEHOLDER, getErrorMessage } from './constants'
import Search from '../../../elements/icons/Search'
import Upload from '../../../elements/icons/Upload'
import Download from '../../../elements/icons/Download'
import { DisplayMessage } from '../../../Components'
import { Loader } from '../../../Components'
import { eventService } from '../../../Services/EventService'
import { connect } from 'react-redux'
import _ from 'lodash'
import * as messageTypes from '../../../utils/messageTypes'

const SiteEventViewer = (props) => {
  const classes = useStyles()

  const [metaData, setMetaData] = useState({
    serverTimeStamp: new Date().toISOString(),
    rowCount: 0
  })
  const [reqBody, setReqBody] = useState(REQUEST_BODY_PLACEHOLDER)
  const [selectedFile, setSelectedFile] = useState(null)
  const [fileName, setFileName] = useState('No file chosen')
  const [loading, setLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [eventsData, setEventsData] = useState([])
  const [filteredEvents, setFilteredEvents] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [statuses, setStatuses] = useState([])
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC)
  const [tableHeadings, setTableHeadings] = useState(EventTableHeadings)
  const [sortedEvents, setSortedEvents] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const eventsTableRef = useRef(null)

  useEffect(() => {
    if (props.isPopup && props.initialData) {
      const data = props.initialData
      setMetaData({
        serverTimeStamp: data.meta?.serverTimeStamp || new Date().toISOString(),
        rowCount: data.meta?.rowCount || (data.data?.length || 0)
      })
      setEventsData(data.data || [])
      setFilteredEvents(data.data || [])
      
      const uniqueStatuses = [...new Set((data.data || []).map(event => event.status))].sort()
      setStatuses(uniqueStatuses)
    }
  }, [props.isPopup, props.initialData])

  useEffect(() => {
    if (props.isPopup) {
      setReqBody('')
    }
  }, [props.isPopup])

  const applyFilters = () => {
    let filtered = eventsData

    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(event =>
        event.name?.toLowerCase().includes(search) ||
        event.siteId?.toString().includes(search) ||
        event.status?.toLowerCase().includes(search) ||
        event.subtype?.toLowerCase().includes(search) ||
        event.programkey?.toLowerCase().includes(search)
      )
    }

    if (statusFilter) {
      filtered = filtered.filter(event => event.status === statusFilter)
    }

    setFilteredEvents(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setFilteredEvents(eventsData)
  }

  const exportToCSV = () => {
    if (filteredEvents.length === 0) return

    const headers = [
      '#', 'Name', 'Site ID', 'Event ID', 'Status', 'Subtype', 'Program Key',
      'Start Time', 'End Time', 'Rate (W)', 'Target SOC (%)',
      'Avg Chg Power (W)', 'Avg Dchg Power (W)', 'Avg Dchg (GS Calc)',
      'Start SOC (%)', 'End SOC (%)',
      'Event Requested At', 'Cmd Sent At',
      'GW Cmd Issued At', 'GW Cmd Response At', 'Cmd Ack At',
      'First Reading At', 'Last Reading At',
      'P1', 'P2', 'P3', 'R1', 'R2', 'R3',
      'Command Request ID', 'Intervals Count'
    ]

    const escapeCsvValue = (value) => {
      const stringValue = value == null ? '' : String(value)
      return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue
    }

    const rows = filteredEvents.map((event, index) => [
      index + 1, event.name, event.siteId, event.eventId, event.status, event.subtype, event.programkey,
      event.startTime, event.endTime, event.rateWatt, event.targetSoc,
      event.avgChargePower, event.avgDischargePower, event.avgDischarged,
      event.startSoc ?? '', event.endSoc ?? '',
      event.eventRequestedAt ?? '', event.commandSentAt ?? '',
      event.gwCommandIssuedAt ?? '', event.gwCommandResponseAt ?? '', event.commandAckAt ?? '',
      event.firstReadingAt ?? '', event.lastReadingTimestamp ?? '',
      event.p1 ?? 0, event.p2 ?? 0, event.p3 ?? 0, event.r1 ?? 0, event.r2 ?? 0, event.r3 ?? 0,
      event.commandRequestId, event.intervals?.length ?? 0
    ].map(escapeCsvValue).join(','))

    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `site-events-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    applyFilters()
  }, [searchTerm, statusFilter, eventsData])

  useEffect(() => {
    if (eventsData.length > 0) {
      const uniqueStatuses = [...new Set(eventsData.map(e => e.status))].sort()
      setStatuses(uniqueStatuses)
    }
  }, [eventsData])

  useEffect(() => {
    if (filteredEvents.length > 0) {
      const sorted = sortEvents(filteredEvents, sortBy, sortOrder)
      setSortedEvents(sorted)
    } else {
      setSortedEvents([])
    }
    // reset to first page whenever the dataset changes substantially
    setPage(0)
  }, [filteredEvents, sortBy, sortOrder])

  useEffect(() => {
    // Close all expanded rows when status filter changes
    if (eventsTableRef.current?.closeAllExpandedRows) {
      eventsTableRef.current.closeAllExpandedRows()
    }
  }, [statusFilter])

  const sortEvents = (events, sortField, order) => {
    return [...events].sort((a, b) => {
      let aVal = a[sortField]
      let bVal = b[sortField]

      if (aVal == null) aVal = ''
      if (bVal == null) bVal = ''

      if (['siteId', 'rateWatt', 'targetSoc', 'avgChargePower', 'avgDischargePower', 'avgDischarged', 'startSoc', 'endSoc', 'p1', 'p2', 'p3', 'r1', 'r2', 'r3'].includes(sortField)) {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      }

      if (order === SortOrder.ASC) {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
      }
    })
  }

  const handleSortChange = (header) => {
    const modifiedObj = _.cloneDeep(tableHeadings)
    modifiedObj.forEach((obj, index) => {
      if (obj.id === header) {
        modifiedObj[index].sortOrder = obj.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      } else {
        if (obj.sortOrder !== null) {
          modifiedObj[index].sortOrder = SortOrder.ASC
        }
      }
    })
    const selectedObj = modifiedObj.find((obj) => header === obj.id)
    const selectedTypeAsc = selectedObj.sortOrder === SortOrder.ASC ? SortOrder.ASC : SortOrder.DESC
    setSortBy(header)
    setSortOrder(selectedTypeAsc)
    setTableHeadings(modifiedObj)
  }

  const getAllSiteEvents = (body) => {
    setLoading(true)
    eventService.getAllSiteEvents(body, props.currentUser.account_id)
      .then(data => {
        setMetaData({
          serverTimeStamp: data.meta?.serverTimeStamp || new Date().toISOString(),
          rowCount: data.meta?.rowCount || data.data.length
        })
        setEventsData(data.data || [])
        setFilteredEvents(data.data || [])
        setMessage(`Loaded ${data.data.length} event(s)`)
        setMessageType('success')
        setOpen(true)
        if (eventsTableRef.current?.closeAllExpandedRows) {
          eventsTableRef.current.closeAllExpandedRows()
        }
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load Events'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() =>
        setLoading(false)
      )
  }

  const fetchAndRender = async () => {
    let body = undefined
    try {
      const trimmedReqBody = reqBody.trim()
      body = JSON.parse(trimmedReqBody)
    } catch (err) {
      setMessage('Invalid JSON in Request Body')
      setMessageType('error')
      setOpen(true)
      return
    }
    getAllSiteEvents(body)
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFile(file)
      setFileName(file.name)
    } else {
      setSelectedFile(null)
      setFileName('No file chosen')
    }
  }


  const uploadCSVFile = (file) => {
    setUploadLoading(true)
    eventService.uploadCSVFile(file, props.currentUser.account_id)
      .then(() => {
        setMessage('CSV uploaded successfully')
        setMessageType('success')
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load Events'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() =>
        setUploadLoading(false)
      )
  }

  const uploadCSV = async () => {
    if (!selectedFile) {
      setMessage('Please select a file')
      setMessageType('error')
      setOpen(true)
      return
    }

    uploadCSVFile(selectedFile)
  }

  return (
    <Box>
      <DisplayMessage
        open={open}
        message={message}
        messageType={messageType}
        handleClose={() => setOpen(false)}
      />
      <Loader loading={loading || uploadLoading} />
      { !props.isPopup && (
        <Box className={classes.urlBar}>
          <Box className={classes.urlExtraGroup}>
            <Box className={classes.urlBarLabel}>
              <Box> Request Body <Box component="span" className={classes.labelSubtext}>(JSON object)</Box> </Box>
              <Button
                className={classes.submitBtn}
                onClick={fetchAndRender}
                disabled={loading}
              >
                <Search />
                <Box paddingLeft='10px'>Fetch & Render</Box>
              </Button>
            </Box>
            <TextField
              className={classes.reqTextarea}
              value={reqBody}
              onChange={(e) => setReqBody(e.target.value)}
              multiline
              variant="standard"
              InputProps={{
                disableUnderline: true,
                style: { color: '#e2e8f0', padding: '8px 12px' },
              }}
            />
          </Box>
          <Box className={classes.urlUpload}>
            <Box className={classes.urlUploadRow}>
              <Box className={classes.urlBarLabelRow}>Upload CSV</Box>
              <input
                type="file"
                id="csvFile"
                accept=".csv"
                className={classes.fileInput}
                onChange={handleFileChange}
              />
              <Box component="label" htmlFor="csvFile" className={classes.fileLabel}>
                <Upload />
                Choose File
              </Box>
              <Box component="span" className={`${classes.fileName} ${selectedFile ? classes.fileNameChosen : ''}`}>
                {fileName}
              </Box>
            </Box>
            <Box paddingTop='10px'>
              <Button
                className={classes.uploadBtn}
                onClick={uploadCSV}
                disabled={uploadLoading}
              >
                <Upload />
                <Box paddingLeft='10px'>Upload CSV</Box>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <Box className={classes.container}>
        <Box className={classes.eventViewerHeader}>
          <Box className={classes.sectionTitle}>Events</Box>
          <Box className={classes.metaPills} id="metaPills">
            <Box component="span" className={classes.metaPill}>
              <Box component="span" className={classes.metaPillStrong}>Server Time:</Box> {metaData.serverTimeStamp}
            </Box>
            <Box component="span" className={classes.metaPill}>
              <Box component="span" className={classes.metaPillStrong}>Row Count:</Box> {metaData.rowCount}
            </Box>
          </Box>
        </Box>
        <Box className={classes.filterBar}>
          <TextField
            className={classes.filterInput}
            placeholder="Search name, site ID, status, subtype, program…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
          />
          <Select
            className={classes.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            disableUnderline
            MenuProps={{
              classes: { paper: classes.selectPaper },
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {statuses.map(status => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </Select>
          <Box component="span" className={classes.resultCount}>
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </Box>
          <Button className={classes.exportBtn} onClick={exportToCSV}>
            <Download />
            <Box paddingLeft='10px'>Export CSV</Box>
          </Button>
          <Button className={classes.clearBtn} onClick={clearFilters}>
            ✕ Clear
          </Button>
        </Box>
        <Box className={classes.card}>
          <EventsTable
            tableHeadings={tableHeadings}
            tableContents={sortedEvents}
            sortOrder={sortOrder}
            sortBy={sortBy}
            handleSortChange={handleSortChange}
            onExpandedRowsChange={(methods) => {
              eventsTableRef.current = methods
            }}
            paginate
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(noOfRows) => { setRowsPerPage(noOfRows); setPage(0) }}
          />
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(SiteEventViewer)